import { useCallback, useReducer } from 'react';

import { cfetch } from '@confluence/network';

export type SpaceOrganizerRunStatus = 'COMPLETED' | 'FAILED';

export type SpaceOrganizerRunResult = {
	runId: string;
	status: SpaceOrganizerRunStatus;
	createdSuggestionCount: number;
};

export type UseRunSpaceOrganizerResult = {
	run: () => Promise<SpaceOrganizerRunResult>;
	isRunning: boolean;
	error: Error | undefined;
};

type RunState = {
	isRunning: boolean;
	error: Error | undefined;
};

type RunAction = { type: 'started' } | { type: 'succeeded' } | { type: 'failed'; error: Error };

const initialRunState: RunState = {
	isRunning: false,
	error: undefined,
};

const runReducer = (_state: RunState, action: RunAction): RunState => {
	switch (action.type) {
		case 'started':
			return { isRunning: true, error: undefined };
		case 'succeeded':
			return { isRunning: false, error: undefined };
		case 'failed':
			return { isRunning: false, error: action.error };
		default:
			return _state;
	}
};

const buildSpaceOrganizerRunUrl = (spaceKey: string): string =>
	`/wiki/rest/internal/1.0/spaces/${spaceKey}/organization/runs`;

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

export const runSpaceOrganizer = async (spaceKey: string): Promise<SpaceOrganizerRunResult> => {
	const response = await cfetch(buildSpaceOrganizerRunUrl(spaceKey), {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to run Space Organizer: ${response.status}`);
	}

	const result: SpaceOrganizerRunResult = await response.json();

	if (result.status !== 'COMPLETED') {
		throw new Error(`Failed to run Space Organizer: ${result.status}`);
	}

	return result;
};

export const useRunSpaceOrganizer = (spaceKey: string): UseRunSpaceOrganizerResult => {
	const [state, dispatch] = useReducer(runReducer, initialRunState);

	const run = useCallback(async () => {
		dispatch({ type: 'started' });

		try {
			const result = await runSpaceOrganizer(spaceKey);
			dispatch({ type: 'succeeded' });
			return result;
		} catch (err) {
			const nextError = toError(err);
			dispatch({ type: 'failed', error: nextError });
			throw nextError;
		}
	}, [spaceKey]);

	return { run, isRunning: state.isRunning, error: state.error };
};
